import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
} from "@angular/core";
import moment from "moment";
import { Subject } from "rxjs";
import { takeUntil, switchMap } from "rxjs/operators";
import { ActionsSubject } from "@ngrx/store";
import { ofType } from "@ngrx/effects";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import { StoreExtendedService } from "../../shared/store-extended.service";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "../../auth/auth.service";
import { User } from "../../users/users.interface";
import { HttpClient } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";
import { SyncRequiredDialogComponent } from "../../shared/sync-required-dialog/sync-required-dialog.component";
import { SyncRequiredDialogData } from "../../shared/sync-required-dialog/sync-required-dialog.interface";
import { SyncSuccessDialogComponent } from "../sync-success-dialog/sync-success-dialog.component";
import { MY_FORMATS } from "../../shared/shared.module";
import {
  GetIntegrationSettingsAction,
  ChangeIntegrationSettingsAction,
  CHANGE_INTEGRATION_SETTINGS_SUCCESS,
  CHANGE_INTEGRATION_SETTINGS_FAILURE,
} from "../../settings/settings.actions";
import { IntegrationSettings } from "../../settings/settings.interface";
import { getSettingsSelector } from "../../settings/settings.reducer";
import { IntegrationSettingsService } from "../integration-settings.service";
import { BillingSettings } from "../../settings/settings.interface";
import { environment } from "../../../environments/environment";
import { getCurrentAccount } from "../../accounts/accounts.reducer";
import { EmployeesService } from "../../employees/employees.service";
import { StartCheckingForInstallationInProgressAction } from "../../signature-installation-state/signature-installation-state.actions";

@Component({
  selector: "app-calamari-integration",
  templateUrl: "./calamari-integration.component.html",
  styleUrls: ["./calamari-integration.component.scss"],
  standalone: false,
})
export class CalamariIntegrationComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() calamariExpanded = true;
  @Input() otherAbsenceEnabled = false;
  @Input() billingSettings: BillingSettings | null = null;
  @Input() activeSyncs: {
    googleSyncStateId?: string;
    microsoftSyncStateId?: string;
    calamariSyncStateId?: string;
  } = {};
  @Output() calamariExpandedChange = new EventEmitter<boolean>();
  @Output() absenceEnabledChange = new EventEmitter<boolean>();

  public environment = environment;
  public currentDomain: string | null = null;
  public hasChanges = false;
  private initialSettings: {
    proxy: { active: boolean; activeAll: boolean; emails: string[] } | null;
    internalSignature: boolean;
    absenceEnabled: boolean;
    absenceLabel: string;
    absenceDaysAhead: number;
  } | null = null;

  public isSuperAdmin: boolean;
  public appUser: User | null | undefined = null;

  public lastCalamariSync: Date | undefined;
  public lastCalamariSyncFormatted: string | undefined;

  public proxyEnabled = false;
  public internalSignatureEnabled = false;
  public absenceEnabled = false;
  public absenceLabel = "";
  public absenceDaysAhead = 30;
  private currentSettings: IntegrationSettings | null = null;

  public savingProxy = false;
  private destroy$ = new Subject<void>();

  public calamariSyncStateId: string | null = null;
  public showCalamariProgress = false;

  public isCalamariSyncInProgress = false;
  public isCalamariConfigSaving = false;

  public isOverrideInProgress = false;
  public calamariConfig: {
    apiKey: string;
    tenant: string;
    absenceEnabled?: boolean;
    absenceLabel?: string;
    absenceDaysAhead?: number;
    substituteText?: string;
  } = {
    apiKey: "",
    tenant: "",
    absenceEnabled: false,
    absenceLabel: "",
    absenceDaysAhead: 30,
    substituteText: "",
  };
  public hideCalamariApiKey = true;
  public calamariConfigExpanded = false;

  openCalamariConfig(): void {
    this.calamariConfigExpanded = !this.calamariConfigExpanded;
    window.open(
      "https://app-new.calamari.io/clockin/configuration/api",
      "_blank",
    );
  }

  constructor(
    private readonly toastr: ToastrTranslatedService,
    private readonly toastrService: ToastrService,
    private readonly translate: TranslateService,
    private readonly auth: AuthService,
    private readonly store: StoreExtendedService,
    private readonly actions$: ActionsSubject,
    private readonly integrationSettingsService: IntegrationSettingsService,
    private readonly http: HttpClient,
    private readonly employeesService: EmployeesService,
    private readonly dialog: MatDialog,
  ) {
    this.lastCalamariSync = this.auth.appUser.lastCalamariSync;
    if (this.lastCalamariSync) {
      this.lastCalamariSyncFormatted = moment(this.lastCalamariSync).format(
        MY_FORMATS.display.dateInput,
      );
    }
    this.isSuperAdmin = this.auth.isSuperAdmin;

    this.actions$
      .pipe(
        ofType(
          CHANGE_INTEGRATION_SETTINGS_SUCCESS,
          CHANGE_INTEGRATION_SETTINGS_FAILURE,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.savingProxy = false;
      });
  }

  ngOnInit(): void {
    this.appUser = this.auth.appUser;

    this.store.select(getCurrentAccount).subscribe((domain) => {
      this.currentDomain = domain;
    });

    if (this.activeSyncs.calamariSyncStateId) {
      this.calamariSyncStateId = this.activeSyncs.calamariSyncStateId;
      this.showCalamariProgress = true;
      this.isCalamariSyncInProgress = true;
    }

    this.loadCalamariConfig();

    this.store.dispatch(new GetIntegrationSettingsAction());
    this.store
      .select(getSettingsSelector)
      .subscribe((settings: IntegrationSettings | null) => {
        if (!settings) {
          return;
        }
        this.currentSettings = settings;
        this.proxyEnabled = !!settings.proxy?.active;
        this.internalSignatureEnabled = settings.internalSignature ?? false;
        this.absenceLabel = settings.absenceLabel ?? "";
        this.absenceDaysAhead = settings.absenceDaysAhead ?? 30;

        this.initialSettings = {
          proxy: settings.proxy
            ? {
                active: settings.proxy.active,
                activeAll: settings.proxy.activeAll,
                emails: settings.proxy.emails,
              }
            : null,
          internalSignature: this.internalSignatureEnabled,
          absenceEnabled: this.absenceEnabled,
          absenceLabel: this.absenceLabel,
          absenceDaysAhead: this.absenceDaysAhead,
        };
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showFreemiumRestrictionDialog = (plan: "free" | "freemium" | "basic") => {
    this.dialog.open(LimitPlanDialogComponent, {
      width: "500px",
      maxWidth: "90vw",
      data: { plan, type: "general" },
    });
  };

  showSyncRequiredDialog = (
    provider: "GOOGLE" | "MICROSOFT" | "CALAMARI",
    onSync: () => void,
  ) => {
    const data: SyncRequiredDialogData = {
      provider,
      onSync,
    };

    this.dialog.open(SyncRequiredDialogComponent, {
      width: "500px",
      maxWidth: "90vw",
      data,
    });
  };

  private checkForChanges(): void {
    if (!this.initialSettings) {
      return;
    }
    const currentProxy = this.proxyEnabled
      ? {
          active: this.proxyEnabled,
          activeAll: this.initialSettings.proxy?.activeAll ?? false,
          emails: this.initialSettings.proxy?.emails ?? [],
        }
      : null;
    const proxyChanged =
      JSON.stringify(currentProxy) !==
      JSON.stringify(this.initialSettings.proxy);

    this.hasChanges =
      proxyChanged ||
      this.internalSignatureEnabled !==
        this.initialSettings.internalSignature ||
      this.absenceEnabled !== this.initialSettings.absenceEnabled ||
      this.absenceLabel !== this.initialSettings.absenceLabel ||
      this.absenceDaysAhead !== this.initialSettings.absenceDaysAhead;
  }

  public saveIntegrationSettings(): void {
    if (!this.currentSettings) {
      this.toastr.error(
        "settings.integration.calamari.serverSide.currentSettings",
      );
      return;
    }
    this.savingProxy = true;
    const updatedSettings: IntegrationSettings = {
      ...this.currentSettings,
      proxy: this.proxyEnabled
        ? {
            active: this.proxyEnabled,
            activeAll: this.initialSettings.proxy?.activeAll ?? false,
            emails: this.initialSettings.proxy?.emails ?? [],
          }
        : {
            active: false,
            activeAll: false,
            emails: [],
          },
      internalSignature: this.internalSignatureEnabled,
      absenceEnabled: this.absenceEnabled,
      absenceLabel: this.absenceLabel || null,
      absenceDaysAhead: this.absenceDaysAhead,
    };
    this.store
      .dispatchAndWaitForResult(
        new ChangeIntegrationSettingsAction(updatedSettings),
      )
      .subscribe(() => {
        this.store.dispatch(new GetIntegrationSettingsAction());
      });
    this.hasChanges = false;
    this.initialSettings = {
      proxy: updatedSettings.proxy
        ? {
            active: updatedSettings.proxy.active,
            activeAll: updatedSettings.proxy.activeAll,
            emails: updatedSettings.proxy.emails,
          }
        : null,
      internalSignature: this.internalSignatureEnabled,
      absenceEnabled: this.absenceEnabled,
      absenceLabel: this.absenceLabel?.trim() || "",
      absenceDaysAhead: this.absenceDaysAhead,
    };
  }

  syncCalamari(): void {
    if (!this.calamariConfig.apiKey || !this.calamariConfig.tenant) {
      this.toastr.error("settings.integration.calamari.configRequired");
      return;
    }

    if (this.isCalamariSyncInProgress) {
      return;
    }

    this.isCalamariSyncInProgress = true;
    this.showCalamariProgress = true;
    this.calamariSyncStateId = null;

    this.integrationSettingsService
      .saveCalamariConfig(this.calamariConfig)
      .pipe(
        switchMap((saveResponse) => {
          if (!saveResponse.success) {
            throw new Error("settings.integration.calamari.configSaveError");
          }
          return this.integrationSettingsService.syncCalamari();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            if (response.calamariSyncStateId) {
              this.calamariSyncStateId = response.calamariSyncStateId;
            } else {
              this.isCalamariSyncInProgress = false;
              this.showCalamariProgress = false;
              this.updateLastCalamariSync();
              this.showSyncSuccessDialog();
            }
          } else {
            if (response.calamariSyncStateId) {
              this.calamariSyncStateId = response.calamariSyncStateId;
            } else {
              this.isCalamariSyncInProgress = false;
              this.showCalamariProgress = false;
              this.toastr.error("settings.integration.calamari.syncError");
            }
          }
        },
        error: (error: { error?: { calamariSyncStateId?: string } }) => {
          if (error?.error?.calamariSyncStateId) {
            this.calamariSyncStateId = error.error.calamariSyncStateId;
          } else {
            this.isCalamariSyncInProgress = false;
            this.showCalamariProgress = false;
            this.toastr.error("settings.integration.calamari.syncError");
          }
        },
      });
  }

  saveCalamariConfig(): void {
    if (!this.calamariConfig.apiKey || !this.calamariConfig.tenant) {
      this.toastr.error("settings.integration.calamari.configRequired");
      return;
    }

    this.isCalamariConfigSaving = true;

    this.integrationSettingsService
      .saveCalamariConfig(this.calamariConfig)
      .subscribe({
        next: (response) => {
          this.isCalamariConfigSaving = false;
          if (response.success) {
            this.toastr.success("settings.integration.calamari.configSaved");
          } else {
            this.toastr.error("settings.integration.calamari.configSaveError");
          }
        },
        error: () => {
          this.isCalamariConfigSaving = false;
          this.toastr.error("settings.integration.calamari.configSaveError");
        },
      });
  }

  private loadCalamariConfig(): void {
    this.integrationSettingsService.getCalamariConfig().subscribe({
      next: (response) => {
        if (response.success && response.config) {
          this.calamariConfig = {
            apiKey: response.config.apiKey || "",
            tenant: response.config.tenant || "",
            absenceEnabled: response.config.absenceEnabled || false,
            absenceLabel: response.config.absenceLabel || "",
            absenceDaysAhead: response.config.absenceDaysAhead || 30,
            substituteText: response.config.substituteText || "",
          };
        } else {
          this.calamariConfig = {
            apiKey: "",
            tenant: "",
            absenceEnabled: false,
            absenceLabel: "",
            absenceDaysAhead: 30,
            substituteText: "",
          };
        }
      },
      error: () => {
        this.calamariConfig = {
          apiKey: "",
          tenant: "",
          absenceEnabled: false,
          absenceLabel: "",
          absenceDaysAhead: 30,
          substituteText: "",
        };
      },
    });
  }

  overrideEmployeesFromCalamari(): void {
    this.isOverrideInProgress = true;

    this.store.dispatch(new StartCheckingForInstallationInProgressAction());

    this.integrationSettingsService.overrideEmployeesFromCalamari().subscribe({
      next: (response) => {
        this.isOverrideInProgress = false;
        if (response.success) {
          this.toastr.success("settings.integration.syncSuccess.title");
        } else {
          this.toastr.error("settings.integration.calamari.overrideError");
        }
      },
      error: () => {
        this.isOverrideInProgress = false;
        this.toastr.error("settings.integration.calamari.overrideError");
      },
    });
  }

  closeCalamariProgress(): void {
    this.isCalamariSyncInProgress = false;
    this.showCalamariProgress = false;
    this.calamariSyncStateId = null;
  }

  onCalamariSyncCompleted(event: { success: boolean }): void {
    this.isCalamariSyncInProgress = false;
    if (!event.success) {
      return;
    }
    this.updateLastCalamariSync();
    void this.auth.fetchCurrent().then(() => {
      const synced = this.auth.appUser?.lastCalamariSync;
      if (!synced) {
        return;
      }
      this.lastCalamariSync = new Date(synced);
      this.lastCalamariSyncFormatted = moment(this.lastCalamariSync).format(
        MY_FORMATS.display.dateInput,
      );
    });
    this.showSyncSuccessDialog();
  }

  private updateLastCalamariSync(): void {
    this.lastCalamariSync = new Date();
    this.lastCalamariSyncFormatted = moment(this.lastCalamariSync).format(
      MY_FORMATS.display.dateInput,
    );
    if (this.auth.appUser) {
      this.auth.appUser.lastCalamariSync = this.lastCalamariSync;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeSyncs && changes.activeSyncs.currentValue) {
      const activeSyncs = changes.activeSyncs.currentValue as {
        calamariSyncStateId?: string;
      };
      if (activeSyncs.calamariSyncStateId) {
        this.calamariSyncStateId = activeSyncs.calamariSyncStateId;
        this.showCalamariProgress = true;
        this.isCalamariSyncInProgress = true;
      }
    }
  }

  toggleCalamariAbsence(checked: boolean): void {
    if (checked && this.otherAbsenceEnabled) {
      return;
    }

    if (checked && !this.lastCalamariSync) {
      this.showSyncRequiredDialog("CALAMARI", () => this.syncCalamari());
      return;
    }

    this.calamariConfig.absenceEnabled = checked;
    this.absenceEnabledChange.emit(this.calamariConfig.absenceEnabled);
    this.checkForChanges();
  }

  private showSyncSuccessDialog(): void {
    this.dialog.open(SyncSuccessDialogComponent, {
      width: "500px",
      disableClose: true,
      autoFocus: false,
      panelClass: "app-dialog-panel",
    });
  }
}
